import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TypeAnimation } from "react-type-animation";
import { MdArrowForward, MdArrowOutward } from "react-icons/md";

/* ── Data ───────────────────────────────────────────────── */
const TECH_STACK = [
    {
        category: "Cloud & Monitoring",
        items: ["Azure Monitor", "Application Insights", "Azure Log Analytics", "Performance Monitoring", "Availability Monitoring", "Azure Cost Management"],
    },
    {
        category: "Observability & SRE",
        items: ["Telemetry Analysis", "Root Cause Analysis", "Incident Management", "KQL (Kusto)", "Metrics & Logs", "Alerting & Dashboards"],
    },
    {
        category: "Development",
        items: ["JavaScript", "React.js", "Node.js", "Express.js", "MongoDB", "MySQL"],
    },
    {
        category: "Tools & Process",
        items: ["Git & GitHub", "VS Code", "Azure Portal", "ITIL Processes", "Technical Documentation", "SOPs & Workflows"],
    },
];

const ABOUT_SKILLS = [
    "Azure Monitor", "Application Insights", "Telemetry", "Observability",
    "Performance", "Availability", "Cost Management", "Root Cause Analysis",
    "ITIL", "Full-Stack", "React.js", "Node.js",
];

const METRICS = [
    { value: "99.9%", label: "Uptime Target" },
    { value: "Feb 2025", label: "Current Role Since" },
    { value: "Azure", label: "Primary Platform" },
    { value: "Honeywell", label: "Enterprise Client" },
];

/* ── Hero Terminal ───────────────────────────────────────── */
function HeroTerminal() {
    const [uptime, setUptime] = useState(0);
    const cardRef = React.useRef(null);
    const rafRef = React.useRef(null);
    const targetRef = React.useRef({ x: 0, y: 0 });
    const currentRef = React.useRef({ x: 0, y: 0 });
    const isHovering = React.useRef(false);

    const messages = [
        "Monitoring system health... stable",
        "Analyzing performance metrics... optimized",
        "Processing telemetry data... complete",
        "Root cause analysis... resolved",
        "Checking availability targets... 99.9%",
        "Reviewing cost metrics... within budget",
        "Log analytics query... results ready",
    ];

    useEffect(() => {
        const start = Date.now();
        const id = setInterval(() => setUptime(Math.floor((Date.now() - start) / 1000)), 1000);
        return () => clearInterval(id);
    }, []);

    // Smooth lerp animation loop
    useEffect(() => {
        const lerp = (a, b, t) => a + (b - a) * t;
        const animate = () => {
            const speed = isHovering.current ? 0.08 : 0.06;
            currentRef.current.x = lerp(currentRef.current.x, targetRef.current.x, speed);
            currentRef.current.y = lerp(currentRef.current.y, targetRef.current.y, speed);

            if (cardRef.current) {
                cardRef.current.style.transform =
                    `perspective(800px) rotateX(${currentRef.current.x}deg) rotateY(${currentRef.current.y}deg)`;
            }
            rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafRef.current);
    }, []);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        targetRef.current.x = -(y - 0.5) * 6;  // max ±3 deg
        targetRef.current.y =  (x - 0.5) * 6;
        isHovering.current = true;
    };

    const handleMouseLeave = () => {
        targetRef.current = { x: 0, y: 0 };
        isHovering.current = false;
    };

    return (
        <div
            ref={cardRef}
            className="w-full max-w-2xl rounded-card p-5 font-mono text-[0.8125rem] leading-relaxed mt-10 text-left will-change-transform"
            style={{
                background: "var(--terminal-bg)",
                WebkitBackdropFilter: "blur(24px) saturate(1.6)",
                backdropFilter: "blur(24px) saturate(1.6)",
                boxShadow: "var(--terminal-shadow)",
                border: "1px solid var(--terminal-border)",
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            {/* Terminal top bar */}
            <div className="flex items-center gap-2 mb-4">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
                <span className="ml-auto flex items-center gap-2 text-on-surface-variant text-[0.72rem]">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
                    session: live · uptime {uptime}s
                </span>
            </div>
            <pre className="whitespace-pre-wrap text-on-surface-variant">
                <span className="text-primary">❯ </span><span className="text-on-surface">whoami</span>{"\n"}
                {"  "}raviraj — cloud &amp; infra engineer{"\n\n"}
                <span className="text-primary">❯ </span><span className="text-on-surface">focus</span>{"\n"}
                {"  "}azure monitoring · observability · cost efficiency{"\n\n"}
                <span className="text-primary">❯ </span><span className="text-on-surface">status</span>{"\n"}
                {"  "}{messages[uptime % messages.length]}
            </pre>
        </div>
    );
}

/* ── Home Page ───────────────────────────────────────────── */
function Home() {
    useEffect(() => { document.title = "Raviraj Kumar Tiwari — Cloud & Infra Engineer"; }, []);

    return (
        <>
            {/* ── HERO ── */}
            <section
                className="relative min-h-[calc(100vh-4.5rem)] flex flex-col items-center justify-center px-6 py-24"
                style={{ background: "var(--clr-surface)" }}
            >
                {/* Subtle radial glow */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse 70% 55% at 20% 30%, color-mix(in srgb, var(--clr-primary) 10%, transparent), transparent 70%), radial-gradient(ellipse 60% 50% at 80% 70%, color-mix(in srgb, var(--clr-primary-container) 8%, transparent), transparent 65%)",
                    }}
                />
                <div className="relative max-w-3xl mx-auto text-center flex flex-col items-center">
                    <p className="section-label mb-6">
                        Cloud &amp; Infrastructure Engineer · LTIMindtree
                    </p>
                    <h1 className="display-heading mb-6">
                        Building Scalable<br />
                        <span style={{ color: "var(--clr-primary)" }}>Azure Cloud</span> Solutions
                    </h1>
                    <TypeAnimation
                        sequence={[
                            "Cloud Monitoring & Observability", 1800,
                            "Performance & Availability Tracking", 1800,
                            "Telemetry & Application Insights", 1800,
                            "Cost Awareness & Optimization", 1800,
                            "Root Cause Analysis & Incident Resolution", 1800,
                        ]}
                        speed={52}
                        repeat={Infinity}
                        className="text-base text-on-surface-variant max-w-xl"
                    />
                    <div className="mt-10 flex flex-wrap gap-3 justify-center">
                        <Link to="/projects" className="btn-primary">
                            View Projects <MdArrowForward size={18} />
                        </Link>
                        <Link to="/contact" className="btn-secondary">
                            Get in Touch
                        </Link>
                    </div>
                    <HeroTerminal />
                </div>
            </section>

            {/* ── TECHNICAL ARSENAL ── */}
            <section className="py-24 px-6 section-light">
                <div className="max-w-6xl mx-auto">
                    <div className="mb-14 max-w-xl">
                        <p className="section-label mb-3">Technical Arsenal</p>
                        <h2 className="headline">
                            Tools &amp; Technologies
                        </h2>
                        <p className="mt-3 text-sm text-on-surface-variant leading-relaxed">
                            A specialized focus on Azure cloud infrastructure combined with full-stack development precision.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {TECH_STACK.map(({ category, items }) => (
                            <div key={category} className="card-node">
                                <p className="text-[0.72rem] font-semibold uppercase tracking-[0.1em] text-primary mb-4">
                                    {category}
                                </p>
                                <ul className="space-y-2">
                                    {items.map((item) => (
                                        <li key={item} className="flex items-center gap-2 text-sm text-on-surface-variant">
                                            <span
                                                aria-hidden="true"
                                                className="h-1 w-1 rounded-full flex-shrink-0"
                                                style={{ background: "var(--clr-primary)" }}
                                            />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── ABOUT ── */}
            <section className="py-24 px-6" style={{ background: "var(--clr-surface)" }}>
                <div className="max-w-6xl mx-auto">
                    <div className="mb-12">
                        <p className="section-label mb-3">About</p>
                        <h2 className="headline">Who I Am</h2>
                    </div>
                    <div className="grid md:grid-cols-[1fr_300px] gap-12 items-start">
                        {/* Bio */}
                        <div>
                            <div className="space-y-4 text-[0.9375rem] text-on-surface-variant leading-[1.75]">
                                <p>
                                    I am a Cloud &amp; Infrastructure Engineer with hands-on experience in Azure Monitor and Application Insights, currently working with <strong className="text-on-surface font-medium">LTIMindtree</strong> on the Honeywell Lenel Physical Access Control Systems project.
                                </p>
                                <p>
                                    My daily work involves checking how systems behave in real time. I monitor performance, availability, telemetry data, logs, and costs. When issues happen, I analyze data to find the root cause and help prevent repeat incidents.
                                </p>
                                <p>
                                    I also have a background in full-stack development. This helps me understand applications as well as infrastructure, which leads to better monitoring and troubleshooting.
                                </p>
                                <p>
                                    I enjoy working on systems that are reliable, simple, and well understood.
                                </p>
                            </div>
                            <div className="mt-8 flex flex-wrap gap-2">
                                {ABOUT_SKILLS.map((skill) => (
                                    <span key={skill} className="chip">{skill}</span>
                                ))}
                            </div>
                            <div className="mt-8 flex gap-3">
                                <Link to="/experience" className="btn-primary text-sm py-2.5 px-5">
                                    View Experience <MdArrowForward size={16} />
                                </Link>
                                <a
                                    href="/resume.pdf"
                                    download
                                    className="btn-ghost text-sm"
                                >
                                    <MdArrowOutward size={16} /> Download Resume
                                </a>
                            </div>
                        </div>
                        {/* Sidebar */}
                        <div className="space-y-6">
                            <div
                                className="rounded-card p-5"
                                style={{ background: "var(--clr-surface-low)" }}
                            >
                                <p className="section-label mb-4">What I Focus On</p>
                                <ul className="space-y-2 text-sm text-on-surface-variant">
                                    {["Cloud Monitoring & Observability", "Performance & Availability", "Telemetry & Application Insights", "Cost Awareness & Optimization", "Root Cause Analysis & ITIL"].map((f) => (
                                        <li key={f} className="flex items-start gap-2">
                                            <span className="mt-1.5 h-1 w-1 rounded-full flex-shrink-0" style={{ background: "var(--clr-primary)" }} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div
                                className="rounded-card p-5"
                                style={{ background: "var(--clr-surface-low)" }}
                            >
                                <p className="section-label mb-4">How I Work</p>
                                <ul className="space-y-2 text-sm text-on-surface-variant">
                                    {["Prefer clarity over complexity", "Rely on data, not assumptions", "Follow ITIL-based processes", "Learn continuously", "Improve systems step by step"].map((h) => (
                                        <li key={h} className="flex items-start gap-2">
                                            <span className="mt-1.5 h-1 w-1 rounded-full flex-shrink-0" style={{ background: "var(--clr-primary)" }} />
                                            {h}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── METRIC BLOCKS ── */}
            <section className="py-20 px-6 section-container">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {METRICS.map(({ value, label }) => (
                            <div key={label}>
                                <div className="metric-value">{value}</div>
                                <div className="metric-label">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}

export default Home;
